import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    Paper,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { useStyles } from './cashflow.styles';
import { HORIZON_OPTIONS } from './cashflow.constants';
import { createDailyProjection, fmt, computeCreditOwedFromEntries } from './cashflow.utils';
import { getAccountsTotal } from 'core/services/accounts.service';
import { getDailyProjection } from 'core/services/tasks.service';

interface ProjectionTabProps {
    entries: any[];
    selectedList: any;
    login: any;
    updateListLimitsSignal: any;
    isMobile: boolean;
    onNavigateToEntries: () => void;
    onSetOnCreditFilter: (v: boolean) => void;
    onAtRiskDatesChange?: (dates: Set<string>) => void;
    setEntryDateFrom: (v: string) => void;
    setEntryDateTo: (v: string) => void;
}

export const ProjectionTab: React.FC<ProjectionTabProps> = ({
    entries,
    selectedList,
    login,
    updateListLimitsSignal,
    isMobile,
    onNavigateToEntries,
    onSetOnCreditFilter,
    onAtRiskDatesChange,
    setEntryDateFrom,
    setEntryDateTo,
}) => {
    const classes = useStyles();

    const [startingBalance, setStartingBalance] = useState<string>('0');
    const [accountsTotal, setAccountsTotal] = useState<number | null>(null);
    const [loadingTotal, setLoadingTotal] = useState(false);
    const [horizonDays, setHorizonDays] = useState(30);
    const [projSortDir, setProjSortDir] = useState<'asc' | 'desc'>('asc');

    const [projDateFrom, setProjDateFrom] = useState('');
    const [projDateTo, setProjDateTo] = useState('');
    const projTodayRef = React.useRef<HTMLElement | null>(null);
    const projFirstRiskRef = React.useRef<HTMLElement | null>(null);
    const hasAutoScrolled = React.useRef(false);

    const [showWhatIf, setShowWhatIf] = useState(false);
    const [whatIfAmount, setWhatIfAmount] = useState('');
    const [whatIfDate, setWhatIfDate] = useState<Date | null>(new Date());
    const [activeWhatIf, setActiveWhatIf] = useState<{ amount: number; date: Date } | null>(null);

    const [creditOwed, setCreditOwed] = useState(0);

    const [showWhatIfOwed, setShowWhatIfOwed] = useState(false);
    const [whatIfOwedAmount, setWhatIfOwedAmount] = useState('');
    const [activeWhatIfOwed, setActiveWhatIfOwed] = useState<{ amount: number } | null>(null);

    // Sync startingBalance and creditOwed from backend when list changes
    useEffect(() => {
        if (!selectedList) return;
        setStartingBalance(selectedList.startingBalance != null ? String(selectedList.startingBalance) : '0');
        const co = selectedList.creditOwed ?? 0;
        setCreditOwed(co);
    }, [selectedList?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch account total for the "Use account balance" button (manual only — never auto-applies)
    React.useEffect(() => {
        const userId = login?.user?.id;
        if (!userId) return;
        setLoadingTotal(true);
        getAccountsTotal(userId)
            .then((res) => {
                if (res && typeof res.total === 'number') setAccountsTotal(res.total);
            })
            .catch(() => {})
            .finally(() => setLoadingTotal(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login?.user?.id]);

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            projTodayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            hasAutoScrolled.current = true;
        });
        return () => cancelAnimationFrame(raf);
    // Only auto-scroll on mount (equivalent to tab === 0 first render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const balance = parseFloat(startingBalance) || 0;

    // Credit owed computed from entries with onCredit flag
    const entryCreditOwed = useMemo(
        () => computeCreditOwedFromEntries(entries),
        [entries]
    );

    // Total credit owed (for display in summary cards)
    const totalCreditOwed = entryCreditOwed.total + (activeWhatIfOwed?.amount || 0);

    // projectionInflation is computed inside cashProjection useMemo below

    const categoryLimits = useMemo(() => selectedList?.categoryLimits || [], [selectedList]);

    // Projection — fetched from API, with client-side fallback
    const [projection, setProjection] = useState<any[]>([]);

    useEffect(() => {
        const userId = login?.user?.id;
        const computeLocal = () => createDailyProjection(entries, balance, horizonDays, activeWhatIf, categoryLimits);
        if (!userId || !selectedList?.id) {
            setProjection(computeLocal());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const whatIfAmount = activeWhatIf?.amount;
                const whatIfDate = activeWhatIf?.date
                    ? activeWhatIf.date.toISOString().slice(0, 10)
                    : undefined;
                const res = await getDailyProjection(
                    userId,
                    selectedList.id,
                    horizonDays,
                    whatIfAmount,
                    whatIfDate,
                );
                if (!cancelled && Array.isArray(res?.projection)) {
                    setProjection(res.projection);
                } else if (!cancelled) {
                    setProjection(computeLocal());
                }
            } catch {
                if (!cancelled) setProjection(computeLocal());
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login?.user?.id, selectedList?.id, entries, balance, creditOwed, horizonDays, activeWhatIf, categoryLimits]);

    // Pre-compute cash-adjusted rows: start from the user's entered balance,
    // then walk forward using inflows - (outflows + creditInflows) as net cash change.
    // This ignores any inflation the backend may have added to startBalance.
    const cashProjection = useMemo(() => {
        if (projection.length === 0) return [];
        const today = new Date();
        let cashBal = balance;
        return projection.map(row => {
            const cashStart = cashBal;
            const cashNet = row.inflows - row.outflows;
            cashBal = cashStart + cashNet;
            // Compute dateLabel if the API didn't provide it
            const dateLabel = row.dateLabel || (() => {
                const [y, m, d] = row.date.split('-').map(Number);
                const dt = new Date(y, m - 1, d);
                return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: dt.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
            })();
            return { ...row, dateLabel, cashStart, cashEnd: cashBal };
        });
    }, [projection, balance]);

    const cashProjDisplay = useMemo(
        () => projSortDir === 'asc' ? cashProjection : [...cashProjection].reverse(),
        [cashProjection, projSortDir]
    );

    const filteredCashProjDisplay = useMemo(() => {
        let rows = cashProjDisplay;
        if (projDateFrom) rows = rows.filter(r => r.date >= projDateFrom);
        if (projDateTo) rows = rows.filter(r => r.date <= projDateTo);
        return rows;
    }, [cashProjDisplay, projDateFrom, projDateTo]);

    const todayRow = useMemo(() => cashProjection.find(r => r.isToday), [cashProjection]);

    // At-risk detection uses cash balance (not inflated)
    const atRiskCount = cashProjection.filter(r => !r.isPast && r.cashEnd < 0).length;
    const firstAtRiskDate = useMemo(
        () => cashProjection.find(r => !r.isPast && r.cashEnd < 0)?.date ?? null,
        [cashProjection]
    );
    const atRiskDates = useMemo(
        () => new Set(cashProjection.filter(r => !r.isPast && r.cashEnd < 0).map(r => r.date)),
        [cashProjection]
    );

    const cashFutureRows = cashProjection.filter(r => !r.isPast);
    const worstBalance = cashFutureRows.length ? Math.min(...cashFutureRows.map(r => r.cashEnd)) : 0;

    // Notify parent when atRiskDates changes
    useEffect(() => {
        if (onAtRiskDatesChange) {
            onAtRiskDatesChange(atRiskDates);
        }
    }, [atRiskDates, onAtRiskDatesChange]);

    const scrollToToday = useCallback(() => {
        projTodayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    const scrollToFirstRisk = useCallback(() => {
        projFirstRiskRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    const handleBalanceChange = (v: string) => {
        setStartingBalance(v);
    };

    const handleBalanceBlur = () => {
        const v = parseFloat(startingBalance) || 0;
        updateListLimitsSignal.dispatch({ startingBalance: v });
    };

    const handleResetToAccounts = () => {
        if (accountsTotal !== null) {
            setStartingBalance(String(accountsTotal));
            updateListLimitsSignal.dispatch({ startingBalance: accountsTotal });
        }
    };

    const handleApplyWhatIf = () => {
        const amount = parseFloat(whatIfAmount) || 0;
        if (amount > 0 && whatIfDate) setActiveWhatIf({ amount, date: whatIfDate });
        setShowWhatIf(false);
    };

    const handleClearWhatIf = () => {
        setActiveWhatIf(null);
        setWhatIfAmount('');
        setWhatIfDate(new Date());
    };

    const handleApplyWhatIfOwed = () => {
        const amount = parseFloat(whatIfOwedAmount) || 0;
        if (amount > 0) setActiveWhatIfOwed({ amount });
        setShowWhatIfOwed(false);
    };

    const handleClearWhatIfOwed = () => {
        setActiveWhatIfOwed(null);
        setWhatIfOwedAmount('');
    };

    return (
        <Box className={classes.tabPanel} style={{ overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* ── Sticky top section (does not scroll) ── */}
            <Box style={{ flexShrink: 0 }}>
            {/* Today summary */}
            {todayRow && (
                <Paper elevation={0} style={{ display: 'flex', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRight: '1px solid #e2e8f0' }}>
                        <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Today's Start</Typography>
                        <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: todayRow.cashStart < 0 ? '#dc2626' : '#1e293b' }}>
                            {fmt(todayRow.cashStart)}
                        </Typography>
                    </Box>
                    {totalCreditOwed > 0 ? (
                        <>
                            <Box style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRight: '1px solid #e2e8f0' }}>
                                <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Cash</Typography>
                                <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: todayRow.cashEnd < 0 ? '#dc2626' : '#1e293b' }}>
                                    {fmt(todayRow.cashEnd)}
                                </Typography>
                            </Box>
                            <Box style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRight: '1px solid #e2e8f0' }}>
                                <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Credit Owed</Typography>
                                <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#7c3aed' }}>
                                    {fmt(totalCreditOwed)}
                                </Typography>
                            </Box>
                            <Box style={{ flex: 1, textAlign: 'center', padding: '10px 0' }}>
                                <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Cash Balance</Typography>
                                <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: todayRow.cashEnd < 0 ? '#dc2626' : '#16a34a' }}>
                                    {fmt(todayRow.cashEnd)}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Box style={{ flex: 1, textAlign: 'center', padding: '10px 0' }}>
                            <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Cash Balance</Typography>
                            <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: todayRow.cashEnd < 0 ? '#dc2626' : '#16a34a' }}>
                                {fmt(todayRow.cashEnd)}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            )}
            {/* Credit owed summary */}
            {totalCreditOwed > 0 && (
                <Paper elevation={0} style={{ borderRadius: 8, border: '1px solid #ede9fe', padding: '10px 14px', backgroundColor: '#faf5ff' }}>
                    <Box display="flex" alignItems="center">
                        <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7c3aed', flexGrow: 1 }}>
                            Credit Owed
                        </Typography>
                        <Typography
                            style={{ fontSize: '1rem', fontWeight: 700, color: '#7c3aed', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            onClick={() => { onSetOnCreditFilter(true); onNavigateToEntries(); }}
                            title="View on-credit entries"
                        >
                            {fmt(totalCreditOwed)}
                        </Typography>
                    </Box>
                    {activeWhatIfOwed && (
                        <Typography style={{ fontSize: '0.68rem', color: '#f59e0b', marginTop: 4 }}>
                            Includes {fmt(activeWhatIfOwed.amount)} what-if owed
                        </Typography>
                    )}
                </Paper>
            )}
            {/* Controls */}
            <Box className={classes.controlsRow}>
                <Box>
                    <TextField
                        className={classes.balanceInput}
                        label="Starting balance ($)"
                        value={startingBalance}
                        onChange={e => handleBalanceChange(e.target.value)}
                        onBlur={handleBalanceBlur}
                        size="small"
                        type="number"
                        variant="outlined"
                        InputProps={{ inputProps: { step: '0.01' } }}
                    />
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        {loadingTotal ? (
                            <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Loading accounts…</Typography>
                        ) : accountsTotal !== null ? (
                            <>
                                <Button
                                    size="small"
                                    style={{ fontSize: '0.65rem', textTransform: 'none', color: '#2563eb', padding: '0 4px', minWidth: 0 }}
                                    onClick={handleResetToAccounts}
                                >
                                    Use account balance ({fmt(accountsTotal)})
                                </Button>
                            </>
                        ) : (
                            <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                No accounts set up
                            </Typography>
                        )}
                    </Box>
                </Box>
                <FormControl size="small" variant="outlined" className={classes.horizonSelect}>
                    <InputLabel>Horizon</InputLabel>
                    <Select
                        value={horizonDays}
                        onChange={e => setHorizonDays(Number(e.target.value))}
                        label="Horizon"
                    >
                        {HORIZON_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    size="small"
                    className={classes.whatIfBtn}
                    onClick={() => setShowWhatIf(true)}
                >
                    + What if I spend…
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    className={classes.whatIfBtn}
                    style={{ color: '#7c3aed', borderColor: '#c4b5fd' }}
                    onClick={() => setShowWhatIfOwed(true)}
                >
                    + What if I owed…
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    style={{ textTransform: 'none', fontSize: '0.78rem', color: '#475569', borderColor: '#cbd5e1' }}
                    onClick={scrollToToday}
                >
                    Today ↓
                </Button>
                {atRiskCount > 0 && (
                    <Button
                        variant="outlined"
                        size="small"
                        style={{ textTransform: 'none', fontSize: '0.78rem', color: '#dc2626', borderColor: '#fca5a5' }}
                        onClick={scrollToFirstRisk}
                    >
                        ⚠ First shortfall
                    </Button>
                )}
            </Box>

            {/* Projection date filter */}
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <TextField
                    type="date" label="From" value={projDateFrom}
                    onChange={e => setProjDateFrom(e.target.value)}
                    size="small" variant="outlined" InputLabelProps={{ shrink: true }}
                    style={{ width: 148 }} inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                />
                <TextField
                    type="date" label="To" value={projDateTo}
                    onChange={e => setProjDateTo(e.target.value)}
                    size="small" variant="outlined" InputLabelProps={{ shrink: true }}
                    style={{ width: 148 }} inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                />
                {(projDateFrom || projDateTo) && (
                    <Button size="small" style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'none', padding: '2px 6px' }}
                        onClick={() => { setProjDateFrom(''); setProjDateTo(''); }}>
                        Clear
                    </Button>
                )}
            </Box>

            {/* Summary chips */}
            <Box className={classes.summaryRow}>
                {atRiskCount === 0 ? (
                    <Chip size="small" label={`No shortfalls in ${horizonDays} days`} className={classes.safeChip} />
                ) : (
                    <Chip size="small" label={`${atRiskCount} at-risk ${atRiskCount === 1 ? 'day' : 'days'}`} className={classes.riskChip} />
                )}
                {worstBalance < 0 && (
                    <Chip size="small" label={`Worst: ${fmt(worstBalance)}`} className={classes.riskChip} />
                )}
                {activeWhatIf && (
                    <Chip
                        size="small"
                        label={`What-if: ${fmt(activeWhatIf.amount)} on ${activeWhatIf.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        className={classes.whatIfChip}
                        onDelete={handleClearWhatIf}
                    />
                )}
                {totalCreditOwed > 0 && (
                    <Chip
                        size="small"
                        label={`Credit owed: ${fmt(totalCreditOwed)}`}
                        style={{ backgroundColor: '#ede9fe', color: '#6d28d9', fontWeight: 600, fontSize: '0.72rem' }}
                    />
                )}
                {activeWhatIfOwed && (
                    <Chip
                        size="small"
                        label={`What-if owed: ${fmt(activeWhatIfOwed.amount)}`}
                        style={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.72rem' }}
                        onDelete={handleClearWhatIfOwed}
                    />
                )}
            </Box>

            </Box>
            {/* ── Scrollable content ── */}
            <Box style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Projection table */}
            <Paper className={classes.tableWrap} elevation={0}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headCell} style={{ cursor: 'pointer' }}>
                                <TableSortLabel
                                    active
                                    direction={projSortDir}
                                    onClick={() => setProjSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                    className={classes.sortLabel}
                                >
                                    Date
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.headCell} align="right">Start</TableCell>
                            {!isMobile && <TableCell className={classes.headCell} align="right">Inflows</TableCell>}
                            {!isMobile && <TableCell className={classes.headCell} align="right">Outflows</TableCell>}
                            {!isMobile && <TableCell className={classes.headCell} align="right">On Credit</TableCell>}
                            <TableCell className={classes.headCell} align="right">End</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCashProjDisplay.map(row => (
                            <TableRow
                                key={row.date}
                                hover
                                ref={(el) => {
                                    if (row.isToday) projTodayRef.current = el;
                                    if (row.date === firstAtRiskDate) projFirstRiskRef.current = el;
                                }}
                                className={row.atRisk ? classes.atRiskRow : row.whatIf ? classes.whatIfRow : ''}
                                style={{
                                    cursor: 'pointer',
                                    ...(row.isPast ? { opacity: 0.55 } : row.isToday ? { backgroundColor: '#f0fdf4' } : undefined),
                                }}
                                onClick={() => {
                                    setEntryDateFrom(row.date);
                                    setEntryDateTo('');
                                    onNavigateToEntries();
                                }}
                            >
                                <TableCell className={classes.cell}>{row.dateLabel}</TableCell>
                                <TableCell className={classes.cell} align="right" style={{ color: '#94a3b8' }}>
                                    {fmt(row.cashStart)}
                                </TableCell>
                                {!isMobile && (
                                    <TableCell className={`${classes.cell} ${classes.creditText}`} align="right">
                                        {row.inflows > 0 ? `+${fmt(row.inflows)}` : ''}
                                    </TableCell>
                                )}
                                {!isMobile && (() => {
                                    const totalOut = row.outflows;
                                    return (
                                        <TableCell className={`${classes.cell} ${classes.debitText}`} align="right">
                                            {totalOut > 0 ? fmt(-totalOut) : ''}
                                        </TableCell>
                                    );
                                })()}
                                {!isMobile && (
                                    <TableCell className={classes.cell} align="right" style={{ color: '#7c3aed' }}>
                                        {(row.creditInflows || 0) > 0 ? fmt(row.creditInflows) : ''}
                                    </TableCell>
                                )}
                                <TableCell
                                    className={`${classes.cell} ${row.cashEnd < 0 && !row.isPast ? classes.riskBalance : classes.safeBalance}`}
                                    align="right"
                                >
                                    {fmt(row.cashEnd)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            </Box>
            {/* What-if dialog */}
            <Dialog open={showWhatIf} onClose={() => setShowWhatIf(false)} maxWidth="xs" fullWidth>
                <DialogTitle>What if I spend…</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" style={{ gap: '16px', paddingTop: '8px' }}>
                        <TextField
                            label="Amount ($)"
                            value={whatIfAmount}
                            onChange={e => setWhatIfAmount(e.target.value)}
                            type="number"
                            variant="outlined"
                            size="small"
                            autoFocus
                            fullWidth
                        />
                        <DatePicker
                            label="On this date"
                            value={whatIfDate}
                            onChange={d => setWhatIfDate(d)}
                            inputVariant="outlined"
                            size="small"
                            format="MMM d, yyyy"
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowWhatIf(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button
                        onClick={handleApplyWhatIf}
                        color="primary"
                        variant="contained"
                        disabled={!whatIfAmount || !whatIfDate}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* What if I owed dialog */}
            <Dialog open={showWhatIfOwed} onClose={() => setShowWhatIfOwed(false)} maxWidth="xs" fullWidth>
                <DialogTitle>What if I owed…</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" style={{ gap: '16px', paddingTop: '8px' }}>
                        <TextField
                            label="Amount ($)"
                            value={whatIfOwedAmount}
                            onChange={e => setWhatIfOwedAmount(e.target.value)}
                            type="number"
                            variant="outlined"
                            size="small"
                            autoFocus
                            fullWidth
                        />
                        <Typography style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            This simulates putting an amount on credit. It won't affect your cash balance but will increase your total credit owed.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowWhatIfOwed(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button
                        onClick={handleApplyWhatIfOwed}
                        variant="contained"
                        style={{ backgroundColor: '#7c3aed', color: 'white' }}
                        disabled={!whatIfOwedAmount}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
